import React, { memo } from "react";
import { Typography, Tag, Button, Modal, Tooltip, Space } from "antd";
import { DatePicker } from "../../../components/antOverrides";
import Patient from "../../../components/tables/Patient";
import Date from "../../../components/tables/Date";
import ProffesionalEmployee from "../../../components/tables/ProffesionalEmployee";
import EmployeeSelect from "./EmployeeSelect";
import YesNoSelect from "./YesNoSelect";
import moment from "moment";
import {
  FormOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { loadRecord } from "../../houseVisit/houseVisitSlice";
import { getFullName } from "../../../components/util/helpers";
import jsPDF from "jspdf";

const { Text } = Typography;

const onDateChange = (
  date,
  record,
  tableItems,
  setTableItems,
  shouldAddToChangedEvents
) => {
  const foundIndex = tableItems.findIndex((x) => x.id === record.id);
  let arr = [...tableItems];
  const changedDate = date.format();
  const changedRecord = { ...record, date: changedDate };
  arr[foundIndex] = changedRecord;
  setTableItems(arr);
  shouldAddToChangedEvents(changedRecord);
};

const onEmployeeChange = (
  val,
  record,
  tableItems,
  setTableItems,
  shouldAddToChangedEvents
) => {
  const foundIndex = tableItems.findIndex((x) => x.id === record.id);
  let arr = [...tableItems];
  const changedRecord = { ...record, planned_employee_id: val };
  arr[foundIndex] = changedRecord;
  setTableItems(arr);
  shouldAddToChangedEvents(changedRecord);
};

const onPresentedChange = (
  val,
  record,
  tableItems,
  setTableItems,
  shouldAddToChangedEvents
) => {
  const foundIndex = tableItems.findIndex((x) => x.id === record.id);
  const arr = [...tableItems];
  const changedRecord = { ...record, care_giver_was_presented: val };
  arr[foundIndex] = changedRecord;
  setTableItems(arr);
  shouldAddToChangedEvents(changedRecord);
};

const onpPhonevisitChange = (
  val,
  record,
  tableItems,
  setTableItems,
  shouldAddToChangedEvents
) => {
  const foundIndex = tableItems.findIndex((x) => x.id === record.id);
  const arr = [...tableItems];
  const changedRecord = {
    ...record,
    report: { ...record.report, phone_visit: val },
  };
  arr[foundIndex] = changedRecord;
  setTableItems(arr);
  shouldAddToChangedEvents(changedRecord);
};

const onShowFormClick = (
  record,
  dispatch,
  setVisible,
  setMode,
  setFormType,
  location
) => {
  const isAdmin = location.pathname.includes("admin");
  const params = {
    patientId: record.patient.id,
    branchId: record.patient.branch,
    admin: isAdmin,
    houseVisitsId: record.id,
  };
  dispatch(loadRecord(params));
  setVisible(true);
  setMode("edit");
  setFormType("houseVisit");
};

const onPrintClick = async (
  record,
  organization,
  intl,
  userBranches,
  loadImage,
  getHouseVisit
) => {
  const avatar_url = organization.avatar_url;
  const logo = await loadImage(avatar_url);
  let diaryPdf = new jsPDF({ orientation: "p" });
  let approvalImg = null;
  let verifyImg = null;
  if (record.approval_signature) {
    approvalImg = await loadImage(record.approval_signature.url);
  }
  if (record.verification_signature) {
    verifyImg = await loadImage(record.verification_signature.url);
  }
  const pdf = await getHouseVisit(
    diaryPdf,
    intl,
    record,
    userBranches,
    organization,
    logo,
    approvalImg,
    verifyImg
  );
  return window.open(pdf.output("bloburl"), "_blank");
};

const onDeleteHouseVisit = (record, intl, dispatch, deleteRecord, loadPage) => {
  const title = intl.formatMessage({
    id: "app.house_visits.table.deleteHouseVisitTitle",
  });
  const content = intl.formatMessage({
    id: "app.house_visits.table.deleteHouseVisitContent",
  });
  Modal.confirm({
    title: title,
    icon: <ExclamationCircleOutlined />,
    content: content,
    onOk() {
      return onConfirm(record, dispatch, deleteRecord, loadPage);
    },
    onCancel() {},
  });
};

const onConfirm = async (record, dispatch, deleteRecord, loadPage) => {
  const params = {
    patientId: record.patient.id,
    branchId: record.patient.branch,
    houseVisitId: record.id,
  };
  const action = await dispatch(deleteRecord(params));
  if (!action.error) {
    dispatch(loadPage());
  }
};

const disabledDate = (current, record) => {
  return (
    current && (current < moment(record.last_visit_time) || current.day() === 6)
  );
};

const RenderPatient = memo(({ text }) => (
  <Tooltip title={text && text.local_id_number}>
    <div>
      <Patient record={text} />
    </div>
  </Tooltip>
));
const RenderCity = memo(({ record }) => (
  <Tooltip title={record?.patient?.address?.city}>
    <Text>{record?.patient?.address?.city}</Text>
  </Tooltip>
));

const RenderAddress = memo(({ record }) => {
  const address =
    record?.patient?.address &&
    `${record.patient.address.street} ${record.patient.address.house_number}`;
  return (
    <Tooltip title={address}>
      <Text>{address}</Text>
    </Tooltip>
  );
});

const RenderDate = memo(
  ({
    intl,
    text,
    record,
    overlapping,
    tableItems,
    setTableItems,
    shouldAddToChangedEvents,
  }) =>
    record.approved ? (
      <Date text={text} format={"HH:mm"} danger={overlapping} />
    ) : overlapping ? (
      <Tooltip
        title={`${intl.formatMessage({
          id: "app.house_visits.table.overlapping1",
        })} ${record?.overlapping?.patient_surename} ${
          record?.overlapping?.patient_first_name
        } (${record?.overlapping?.branch_name}), ${intl.formatMessage({
          id: "app.house_visits.table.overlapping2",
        })} ${moment(record?.overlapping?.date).format("HH:mm")}-${moment(
          record?.overlapping?.date
        )
          .add(record?.overlapping?.duration_minutes, "minutes")
          .format("HH:mm")}`}
      >
        <DatePicker
          value={moment(text)}
          format={"HH:mm"}
          picker="time"
          onChange={(time) =>
            onDateChange(
              time,
              record,
              tableItems,
              setTableItems,
              shouldAddToChangedEvents
            )
          }
          allowClear={false}
          inputReadOnly={false}
          disabled={record.approved}
          status={"error"}
        />
      </Tooltip>
    ) : (
      <DatePicker
        value={moment(text)}
        format={"HH:mm"}
        picker="time"
        onChange={(time) =>
          onDateChange(
            time,
            record,
            tableItems,
            setTableItems,
            shouldAddToChangedEvents
          )
        }
        allowClear={false}
        inputReadOnly={false}
        disabled={record.approved}
      />
    )
);

export const getColumns = (
  tableItems,
  setTableItems,
  shouldAddToChangedEvents,
  intl,
  dispatch,
  organization,
  userBranches,
  loadImage,
  getHouseVisit,
  deleteRecord,
  loadPage,
  setVisible,
  setMode,
  setFormType,
  location,
  showLocalId
) => {
  let columns = [
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.patient",
      }),
      dataIndex: "patient",
      width: "180px",
      fixed: "left",
      render(text, record) {
        return <RenderPatient text={text} />;
      },
      sorter: (a, b) =>
        a["patient"].surename.localeCompare(b["patient"].surename),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.city",
      }),
      dataIndex: "city",
      width: "80px",
      render(text, record) {
        return <RenderCity record={record} />;
      },
      sorter: (a, b) =>
        a["patient"].address.city.localeCompare(b["patient"].address.city),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.address",
      }),
      dataIndex: "street",
      width: "120px",
      render(text, record) {
        return <RenderAddress record={record} />;
      },
      sorter: (a, b) =>
        a["patient"].address.street.localeCompare(b["patient"].address.street),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.last_visit_time",
      }),
      dataIndex: "last_visit_time",
      width: "120px",
      type: "date",
      required: true,
      render(text, record) {
        return <Date text={text} format={"L"} />;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.plannedDate",
      }),
      dataIndex: "date",
      width: "160px",
      render(text, record) {
        return record.approved ? (
          <Date text={text} format={"L"} />
        ) : (
          <DatePicker
            value={moment(text)}
            format={"L"}
            onChange={(time) =>
              onDateChange(
                time,
                record,
                tableItems,
                setTableItems,
                shouldAddToChangedEvents
              )
            }
            allowClear={false}
            inputReadOnly={false}
            disabledDate={(current) => disabledDate(current, record)}
            disabled={record.approved}
          />
        );
      },
      sorter: (a, b) => a["date"].localeCompare(b["date"]),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.planneHour",
      }),
      dataIndex: "date",
      width: "120px",
      render(text, record) {
        return (
          <RenderDate
            intl={intl}
            text={text}
            record={record}
            overlapping={record.overlapping}
            tableItems={tableItems}
            setTableItems={setTableItems}
            shouldAddToChangedEvents={shouldAddToChangedEvents}
          />
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.overflow_months_number",
      }),
      dataIndex: "overflow_months_number",
      width: "86px",
      render(text, record) {
        return text > 0 ? (
          <CloseCircleOutlined style={{ color: "red" }} />
        ) : (
          <CheckCircleOutlined style={{ color: "green" }} />
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.last_visit_executed_by",
      }),
      dataIndex: "last_visit_executed_by",
      width: "130px",
      render(text, record) {
        return <ProffesionalEmployee record={text} />;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.plannedEmployee",
      }),
      dataIndex: "planned_employee_id",
      width: "180px",
      render(text, record) {
        return (
          <EmployeeSelect
            popupContainer={document.getElementById("visitsTableRef")}
            initialName={
              record.employee
                ? record.employee.professional_role_id
                  ? `${getFullName(
                      record.employee.surename,
                      record.employee.first_name
                    ).concat(
                      ", ",
                      intl.formatMessage({
                        id: `app.dataSelect.professional_role_id.${record.employee.professional_role_id}`,
                      })
                    )}`
                  : `${getFullName(
                      record.employee.surename,
                      record.employee.first_name
                    )}`
                : null
            }
            branch={record.patient.branch}
            onChange={(val) =>
              onEmployeeChange(
                val,
                record,
                tableItems,
                setTableItems,
                shouldAddToChangedEvents
              )
            }
            allowClear={false}
            disabled={record.approved}
            error={record.professional_employee_missing_error}
          />
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.last_visit_care_giver_presented",
      }),
      dataIndex: "last_visit_care_giver_presented",
      width: "80px",
      render(text, record) {
        return (
          <Text>
            {text
              ? intl.formatMessage({
                  id: "app.forms.yes",
                })
              : intl.formatMessage({
                  id: "app.forms.no",
                })}
          </Text>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.care_giver_was_presented",
      }),
      dataIndex: "care_giver_was_presented",
      width: "100px",
      render(text, record) {
        return (
          <YesNoSelect
            popupContainer={document.getElementById("visitsTableRef")}
            error={record.care_giver_not_presented_error}
            name="care_giver_was_presented"
            value={text}
            onChange={(val) =>
              onPresentedChange(
                val,
                record,
                tableItems,
                setTableItems,
                shouldAddToChangedEvents
              )
            }
            disabled={record.approved}
          />
        );
      },
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.phone_visit",
      }),
      dataIndex: "phone_visit",
      width: "100px",
      render(text, record) {
        return (
          <YesNoSelect
            popupContainer={document.getElementById("visitsTableRef")}
            name="phone_visit"
            value={text}
            onChange={(val) =>
              onpPhonevisitChange(
                val,
                record,
                tableItems,
                setTableItems,
                shouldAddToChangedEvents
              )
            }
            disabled={record.approved}
          />
        );
      },
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.visitApproved",
      }),
      dataIndex: "approved",
      width: "95px",
      fixed: "right",
      render(text, record) {
        return text === true ? (
          <Tooltip
            title={
              record["approval_signature"] &&
              record["approval_signature"].url && (
                <FormattedMessage id="app.house_visits.table.signed_digitally" />
              )
            }
          >
            <Tag
              color="success"
              icon={
                record["approval_signature"] &&
                record["approval_signature"].url ? (
                  <SafetyCertificateOutlined />
                ) : null
              }
            >
              {intl.formatMessage({
                id: "app.house_visits.table.approved",
              })}
            </Tag>
          </Tooltip>
        ) : text === false ? (
          <Tag color="error">
            {intl.formatMessage({
              id: "app.house_visits.table.notApproved",
            })}
          </Tag>
        ) : null;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.verify",
      }),
      dataIndex: "verified",
      width: "90px",
      fixed: "right",
      render(text, record) {
        return record["verified"] ? (
          <Tooltip
            title={
              record["verification_signature"] &&
              record["verification_signature"].url && (
                <FormattedMessage id="app.house_visits.table.signed_digitally" />
              )
            }
          >
            <Tag
              color="processing"
              icon={
                record["verification_signature"] &&
                record["verification_signature"].url ? (
                  <SafetyCertificateOutlined />
                ) : null
              }
            >
              {intl.formatMessage({
                id: "app.house_visits.table.verified",
              })}
            </Tag>
          </Tooltip>
        ) : null;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.actions",
      }),
      key: "action",
      width: "150px",
      fixed: "right",
      render: (text, record) => (
        <Space size="middle">
          <Tooltip
            title={<FormattedMessage id="app.house_visits.table.editTooltip" />}
          >
            <Button
              onClick={() =>
                onShowFormClick(
                  record,
                  dispatch,
                  setVisible,
                  setMode,
                  setFormType,
                  location
                )
              }
              type="text"
              icon={<FormOutlined />}
            />
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage id="app.house_visits.table.printTooltip" />
            }
          >
            <Button
              onClick={() =>
                onPrintClick(
                  record,
                  organization,
                  intl,
                  userBranches,
                  loadImage,
                  getHouseVisit
                )
              }
              type="text"
              icon={<PrinterOutlined />}
            />
          </Tooltip>
          {record["approved"] || record["report.filled"] ? null : (
            <Tooltip
              title={
                <FormattedMessage id="app.house_visits.table.deleteTooltip" />
              }
            >
              <Button
                style={{ color: "red" }}
                onClick={() =>
                  onDeleteHouseVisit(
                    record,
                    intl,
                    dispatch,
                    deleteRecord,
                    loadPage
                  )
                }
                type="text"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (showLocalId) {
    columns.splice(1, 0, {
      title: intl.formatMessage({
        id: "app.house_visits.table.local_id",
      }),
      dataIndex: "patient",
      width: "120px",
      render(text, record) {
        return <Text>{text && text.local_id_number}</Text>;
      },
    });
  }
  return columns;
};
